import React, { useEffect, useMemo, useRef, useState } from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import ListingsMap, { Neighborhood } from "./ListingsMap";
import PostcodeMap from "./PostcodeMap";
import CommuteChipsList from "./CommuteChipsList";
import { PostCode } from "./services/listingsSearchCriteriaApi";
import { PostcodeSelectionState } from "./postcodeUtils";
import { JourneyData } from "./services/tflApi";
import { themeJson } from "./theme";
import "./MapBottomSheet.css";

type Props = {
    neighborhoods: Neighborhood[];
    selectedIds: string[];
    onToggle?: (id: string, source: 'map' | 'form') => void;
    onClose: () => void;
    locationMode: 'neighborhood' | 'postcode' | 'commute';
    onModeChange: (mode: 'neighborhood' | 'postcode') => void;
    postcodes: PostCode[];
    postcodeSelection: PostcodeSelectionState;
    onTogglePostcode: (code: string) => void;
    journeyMap?: Map<string, JourneyData>;
};

const SNAP_COLLAPSED = 60;
const SNAP_EXPANDED = 95;
const CLOSE_THRESHOLD = 35;

const locationsOnlySurvey = {
    showNavigationButtons: false,
    showQuestionNumbers: "off" as const,
    pages: [{
        name: "mapPage",
        elements: [{
            type: "tagbox",
            name: "locations",
            titleLocation: "hidden",
            choices: [],
            placeholder: "Type or select an option",
            autocomplete: "name",
        }],
    }],
};

export default function MapBottomSheet({
    neighborhoods,
    selectedIds,
    onToggle,
    onClose,
    locationMode,
    onModeChange,
    postcodes,
    postcodeSelection,
    onTogglePostcode,
    journeyMap,
}: Props) {
    const syncing = useRef(false);
    const [sheetHeight, setSheetHeight] = useState(0);
    const [visible, setVisible] = useState(false);
    const [dragging, setDragging] = useState(false);

    const dragRef = useRef({ startY: 0, startHeight: 0, isDragging: false });

    // -- SurveyJS mini model (neighbourhood mode only) --
    const miniSurvey = useMemo(() => {
        const s = new Model(locationsOnlySurvey);
        s.elementIdPrefix = "map-sheet-";
        s.applyTheme(themeJson);
        return s;
    }, []);

    // Populate choices from neighborhoods
    useEffect(() => {
        if (locationMode === 'postcode') return;
        const q = miniSurvey.getQuestionByName("locations");
        if (q) {
            q.choices = neighborhoods.map(n => ({ value: n.id, text: n.name }));
        }
    }, [miniSurvey, neighborhoods, locationMode]);

    // Sync selectedIds -> miniSurvey
    useEffect(() => {
        if (locationMode === 'postcode') return;
        if (syncing.current) return;
        syncing.current = true;
        miniSurvey.setValue("locations", selectedIds.length ? selectedIds : undefined);
        syncing.current = false;
    }, [miniSurvey, selectedIds, locationMode]);

    // Sync miniSurvey -> parent via onToggle
    useEffect(() => {
        if (locationMode === 'postcode') return;
        const handler = (sender: Model) => {
            if (syncing.current) return;
            syncing.current = true;
            const newIds: string[] = sender.data.locations || [];
            const added = newIds.filter(id => !selectedIds.includes(id));
            const removed = selectedIds.filter(id => !newIds.includes(id));
            for (const id of added) onToggle?.(id, 'form');
            for (const id of removed) onToggle?.(id, 'form');
            syncing.current = false;
        };
        miniSurvey.onValueChanged.add(handler);
        return () => miniSurvey.onValueChanged.remove(handler);
    }, [miniSurvey, selectedIds, onToggle, locationMode]);

    // Body scroll lock
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    // Enter animation: start at 0, transition to SNAP_COLLAPSED
    useEffect(() => {
        requestAnimationFrame(() => {
            setVisible(true);
            setSheetHeight(SNAP_COLLAPSED);
        });
    }, []);

    // -- Drag handle touch events --
    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        dragRef.current = { startY: touch.clientY, startHeight: sheetHeight, isDragging: true };
        setDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!dragRef.current.isDragging) return;
        const touch = e.touches[0];
        const deltaY = dragRef.current.startY - touch.clientY;
        const deltaVh = (deltaY / window.innerHeight) * 100;
        const newHeight = Math.max(0, Math.min(100, dragRef.current.startHeight + deltaVh));
        setSheetHeight(newHeight);
    };

    const handleTouchEnd = () => {
        if (!dragRef.current.isDragging) return;
        dragRef.current.isDragging = false;
        setDragging(false);

        if (sheetHeight < CLOSE_THRESHOLD) {
            onClose();
            return;
        }

        // Snap to nearest snap point
        const distToCollapsed = Math.abs(sheetHeight - SNAP_COLLAPSED);
        const distToExpanded = Math.abs(sheetHeight - SNAP_EXPANDED);
        setSheetHeight(distToCollapsed <= distToExpanded ? SNAP_COLLAPSED : SNAP_EXPANDED);
    };

    const selectedPostcodeSummary = useMemo(() => {
        if (postcodeSelection.selectedCodes.size === 0) return null;
        return Array.from(postcodeSelection.selectedCodes).join(", ");
    }, [postcodeSelection.selectedCodes]);

    return (
        <>
            <div
                className={`map-bottom-sheet-backdrop${visible ? "" : " map-bottom-sheet-backdrop--hidden"}`}
                onClick={onClose}
            />
            <div
                className="map-bottom-sheet"
                style={{
                    height: `${sheetHeight}vh`,
                    transition: dragging ? "none" : undefined,
                }}
            >
                <div
                    className="map-bottom-sheet-handle"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="map-bottom-sheet-handle-pill" />
                </div>

                {locationMode !== 'commute' && (
                    <div style={{
                        display: "flex",
                        margin: "4px 16px 0",
                        background: "#eef0f4",
                        borderRadius: "10px",
                        padding: "3px",
                        gap: "2px",
                        flexShrink: 0,
                    }}>
                        {(["neighborhood", "postcode"] as const).map(mode => (
                            <button
                                key={mode}
                                onClick={() => onModeChange(mode)}
                                style={{
                                    flex: 1,
                                    padding: "7px 0",
                                    border: "none",
                                    borderRadius: "8px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition: "background 0.15s, color 0.15s",
                                    background: locationMode === mode ? "#fff" : "transparent",
                                    color: locationMode === mode ? "#5271FF" : "#777",
                                    boxShadow: locationMode === mode ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                }}
                            >
                                {mode === "neighborhood" ? "Neighbourhood" : "Postcode"}
                            </button>
                        ))}
                    </div>
                )}

                <div className="map-bottom-sheet-tagbox">
                    {locationMode === 'commute' ? (
                        <CommuteChipsList
                            ids={selectedIds}
                            neighbourhoods={neighborhoods}
                            onRemove={id => onToggle?.(id, 'form')}
                            limit={5}
                            scrollable
                        />
                    ) : locationMode === 'neighborhood' ? (
                        <Survey model={miniSurvey} />
                    ) : (
                        <div style={{ padding: "8px 16px", fontSize: "13px", color: "#555" }}>
                            {selectedPostcodeSummary
                                ? <span><strong>Selected:</strong> {selectedPostcodeSummary}</span>
                                : <span style={{ color: "#aaa" }}>Tap postcode areas on the map</span>
                            }
                        </div>
                    )}
                </div>

                <div className="map-bottom-sheet-map">
                    {locationMode === 'postcode' ? (
                        <PostcodeMap
                            postcodes={postcodes}
                            selectionState={postcodeSelection}
                            onToggle={onTogglePostcode}
                        />
                    ) : (
                        <>
                            {selectedIds.length === 0 && locationMode !== 'commute' && (
                                <div className="map-bottom-sheet-hint">Tap areas on the map</div>
                            )}
                            <ListingsMap
                                neighborhoods={neighborhoods}
                                selectedIds={selectedIds}
                                onToggle={locationMode === 'commute' ? undefined : (id) => onToggle?.(id, 'map')}
                                journeyMap={locationMode === 'commute' ? journeyMap : undefined}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
